import { useEffect, useRef } from "react";

import { useForm } from "react-hook-form";

import {
  FREE_ENTRY_FORM_ID,
  SweepstakesFreeEntryFields,
  SweepstakesFreeEntryFormContent,
} from "./SweepstakesFreeEntryFormContent";

import { trpc } from "@/common/helpers/trpc";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { FormButton } from "@/forms-design-system/FormButton/FormButton";
import { FormDialog } from "@/forms-design-system/FormDialog/FormDialog";

interface SweepstakesFreeEntryDialogProps {
  open: boolean;
  onClose: () => void;
  ticketingId: string;
  ticketingOccurrenceId?: string;
  onConfirmed: (confirmation: { commandId: string; email: string; entryCount: number }) => Promise<void>;
}

const EMPTY_VALUES: SweepstakesFreeEntryFields = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  address: "",
  city: "",
  postalCode: "",
  country: "",
  region: "",
};

const getErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const candidate = error as {
    code?: unknown;
    cause?: { code?: unknown };
    data?: { code?: unknown };
    shape?: { data?: { code?: unknown } };
  };
  return [candidate.cause?.code, candidate.data?.code, candidate.shape?.data?.code, candidate.code].find(
    (code): code is string => typeof code === "string"
  );
};

export const SweepstakesFreeEntryDialog = ({
  open,
  onClose,
  ticketingId,
  ticketingOccurrenceId,
  onConfirmed,
}: SweepstakesFreeEntryDialogProps) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const submissionInFlightRef = useRef(false);
  const form = useForm<SweepstakesFreeEntryFields>({ defaultValues: EMPTY_VALUES, mode: "onBlur" });
  const { mutateAsync: submitFreeEntry, isPending } = trpc.form_submitSweepstakesFreeEntry.useMutation();
  const { executeTrpc } = useRecaptchaRetry();
  const isSubmitting = isPending || form.formState.isSubmitting;
  useEffect(() => {
    if (!open) {
      form.reset(EMPTY_VALUES);
    }
  }, [form, open]);

  const setKnownSubmissionError = (error: unknown): boolean => {
    const errorCode = getErrorCode(error);
    if (errorCode === "sweepstakes_free_entry_limit") {
      form.setError("email", { message: t("ticketing", "sweepstakes.freeEntry.emailLimit") });
      return true;
    }
    if (errorCode === "sweepstakes_free_entry_capacity_reached") {
      form.setError("email", { message: t("ticketing", "sweepstakes.freeEntry.capacityReached") });
      return true;
    }
    return false;
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    if (submissionInFlightRef.current) {
      return;
    }
    submissionInFlightRef.current = true;
    logAmplitudeEvent(AmplitudeEvents.SweepstakesFreeEntrySubmitted, {
      ticketingId,
      is_sweepstakes: true,
    });
    try {
      const result = await executeTrpc(RecaptchaAction.SubmitCommand, values.email, submitFreeEntry, {
        ticketingId,
        ticketingOccurrenceId,
        ...values,
        phoneNumber: `+${values.phoneNumber.replace(/^\++/, "")}`,
      });
      if (setKnownSubmissionError(result.error)) {
        return;
      }
      if (result.error) {
        form.setError("email", { message: t("common", "tryAgainOrContactUs") });
        return;
      }
      if (!result.data) {
        form.setError("email", { message: t("common", "tryAgainOrContactUs") });
        return;
      }
      logAmplitudeEvent(AmplitudeEvents.SweepstakesFreeEntrySucceeded, {
        ticketingId,
        is_sweepstakes: true,
        entry_count: result.data.entryCount,
      });
      await onConfirmed(result.data);
    } catch (error) {
      if (setKnownSubmissionError(error)) {
        return;
      }
      form.setError("email", { message: t("common", "tryAgainOrContactUs") });
    } finally {
      submissionInFlightRef.current = false;
    }
  });

  return (
    <FormDialog
      open={open}
      onClose={() => {
        if (!isSubmitting) {
          onClose();
        }
      }}
      title={t("ticketing", "sweepstakes.freeEntry.title")}
      size="s"
      variant={isSmallScreen ? "drawer" : "default"}
      actions={[
        <FormButton
          key="confirm"
          type="submit"
          form={FREE_ENTRY_FORM_ID}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("common", "confirm")}
        </FormButton>,
        <FormButton key="cancel" type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          {t("common", "cancel")}
        </FormButton>,
      ]}
    >
      <SweepstakesFreeEntryFormContent form={form} onSubmit={handleSubmit} />
    </FormDialog>
  );
};
